import { COMBO_TIERS } from '~/consts';
import { PlayerNote, songBeat, SongTrack } from '~/interfaces';
import {
  bestQualityByNote,
  isHit,
  NoteScope,
  notesInScope,
  qualityPerTrackNote,
} from '~/modules/game-engine/game-state/helpers/calculate-note-stats';

export interface ComboState {
  /** Notes hit in a row, counting back from the most recent. */
  current: number;
  /** The longest run so far this song. */
  best: number;
}

/**
 * Room left after a note has ended before the run is allowed to break on it, on top of the
 * singer's own input lag. Covers the gap between one look at the notes and the next, and the frame
 * the last frequency of the note lands on.
 */
export const JUDGE_GRACE_MS = 250;

/**
 * The beat up to which a singer's notes can be marked. Their frequencies reach the engine behind
 * the song by their own input lag, so judging a note the moment it ends would call one missed that
 * is still on its way - and with every note breaking the run, no combo could ever start.
 */
export const judgedUpToBeat = ({
  currentBeat,
  inputLagMs,
  beatLengthMs,
}: {
  currentBeat: number;
  inputLagMs: number;
  beatLengthMs: number;
}) => currentBeat - (inputLagMs + JUDGE_GRACE_MS) / beatLengthMs;

export const comboTierLabel = (combo: number): string | null =>
  COMBO_TIERS.find(({ from }) => combo >= from)?.label ?? null;

export const comboMultiplier = (combo: number): number =>
  COMBO_TIERS.find(({ from }) => combo >= from)?.multiplier ?? 1;

// The same run the combo counter shows, walked over the same notes, so the multiplier a note scores
// at is the tier on screen when it was sung. Keyed by the note's start beat; a note that did not hit
// has no entry, since it scores nothing to multiply.
export const comboMultiplierByNote = (
  playerNotes: PlayerNote[],
  track: SongTrack | undefined,
  tolerance: number,
  scope: NoteScope = {},
): Map<songBeat, number> => {
  const byNote = new Map<songBeat, number>();
  if (!track) return byNote;

  const { best } = bestQualityByNote(playerNotes, tolerance);
  let run = 0;

  for (const note of notesInScope(track, scope)) {
    if (!isHit(best.get(note.start) ?? 'missed')) {
      run = 0;
      continue;
    }

    run++;
    byNote.set(note.start, comboMultiplier(run));
  }

  return byNote;
};

/**
 * A run is consecutive notes that scored - the same bar the post-song stats use, so the combo shown
 * while singing and the one reported afterwards mean the same thing.
 *
 * Counted over the notes of the track (see `qualityPerTrackNote`), not over the notes the singer
 * attempted: a note nobody sang leaves no trace in `playerNotes` at all, so walking those kept a
 * run alive through a silence of any length and joined the runs either side of a skipped passage.
 */
export const computeComboState = (
  playerNotes: PlayerNote[],
  track: SongTrack | undefined,
  tolerance: number,
  scope: NoteScope = {},
): ComboState => {
  let best = 0;
  let run = 0;

  for (const quality of qualityPerTrackNote(playerNotes, track, tolerance, scope)) {
    // Only the notes that scored keep a run alive - a near miss is still a miss to the scoreboard.
    if (!isHit(quality)) {
      run = 0;
      continue;
    }

    run++;
    best = Math.max(best, run);
  }

  // The run still going at the end of the list is the current one.
  return { current: run, best };
};
